import type { ParseKeys } from 'i18next';
import type { LastModification, TextWithAttachments } from '@/types/diary';

export enum BehaviourEntryType {
  Positive = 'positive',
  Negative = 'negative',
}

export interface BehaviourEntry {
  disciplineName: string;
  type: BehaviourEntryType;
}

export interface TeacherMessage {
  id: string;
  text: string;
  authorName: string;
  date: string;
  typeName: string;
  isNew: boolean;
  behaviorEntry: BehaviourEntry | null;
}

export interface LessonType {
  name: string;
  abbreviation: string | null;
  lessonTypeId: string;
}

export interface EvaluationLesson {
  date: string;
  type: LessonType;
  isTest: boolean;
  subject: string;
}

export interface EditedEvaluation {
  value: string;
  reason: string | null;
  timeEdited: string;
}

export interface Evaluation {
  id: number;
  value: string;
  editedEvaluation: EditedEvaluation | null;
  timeCreated: string;
  disciplineName: string;
  lesson: EvaluationLesson;
  isWeighted: boolean;
  isNew: boolean;
  isNonAttendance: boolean;
  hasComments: boolean;
}

export enum StudyYearPeriodType {
  Year = 'ptid_Year',
  Semester = 'ptid_Semester',
  Month = 'ptid_Month',
  Unknown = 'unknown',
  Custom = 'custom',
}

export interface StudyYearPeriod {
  name: string;
  from: Date;
  to: Date;
  type: StudyYearPeriodType;
}

export enum NonAttendanceType {
  Sick = 's',
  Other = 'c',
}

export enum DisciplineType {
  MandatoryContent = 'MandatoryContent',
  InterestEducation = 'InterestEducation',
  OptionalLesson = 'OptionalLesson',
  ExtendedDay = 'ExtendedDay',
}

export const KNOWN_DISCIPLINE_TYPES: ReadonlySet<DisciplineType> = new Set(
  Object.values(DisciplineType),
);

const LABEL_KEY_PREFIX = 'overview.attendance.disciplineType';

export const disciplineTypeLabelKeys: Record<DisciplineType, ParseKeys> = {
  [DisciplineType.MandatoryContent]: `${LABEL_KEY_PREFIX}.mandatoryContent`,
  [DisciplineType.InterestEducation]: `${LABEL_KEY_PREFIX}.interestEducation`,
  [DisciplineType.OptionalLesson]: `${LABEL_KEY_PREFIX}.optionalLesson`,
  [DisciplineType.ExtendedDay]: `${LABEL_KEY_PREFIX}.extendedDay`,
};

export interface NonAttendance {
  id: number;
  disciplineName: string;
  disciplineType: DisciplineType;
  value: string;
  type: string;
  isNew: boolean;
}

export interface EvaluationDetailLessonType {
  name: string;
  lessonTypeId: string;
}

export interface EvaluationDetailLessonCreation {
  authorName: string;
  timeCreated: string;
}

export interface EvaluationDetailLesson {
  type: EvaluationDetailLessonType;
  lessonDate: string;
  lessonSubject: TextWithAttachments;
  lessonNumberInDiary: number | null;
  creation: EvaluationDetailLessonCreation;
  lastModification: LastModification | null;
  disciplineName: string;
  testId?: number | null;
}

export interface EvaluationDetailEvaluation {
  id: number;
  value: string;
  valueFullName?: string | null;
  nonAttendanceType: string | null;
  authorName: string;
  testNotes: string | null;
  achievementRecordText: string | null;
  evaluationWeight: string | null;
  timeCreated: string;
}

export interface EvaluationStatistics {
  chartItems: number[];
  studentPlace: number | null;
  indexOfStudentEvaluation: number;
  averageClassEvaluation: string | null;
}

export interface EvaluationEdit {
  value: string;
  valueFullName?: string | null;
  nonAttendanceType: string | null;
  authorName: string;
  timeCreated: string;
}

export interface EvaluationDetail {
  evaluation: EvaluationDetailEvaluation;
  lesson: EvaluationDetailLesson;
  statistics: EvaluationStatistics | null;
  editedEvaluations: EvaluationEdit[];
}

export interface NonAttendanceGroup {
  nonAttendances: NonAttendance[];
  date: string;
}

export interface EvaluationsSummary {
  evaluations: Evaluation[];
  nonAttendances: NonAttendanceGroup[];
}
